import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlTextField } from '../../../common/components/AlTextField';
import { InputRow } from './InputRow';
import { datePickerFormat, minBookingDate } from '../../../common/utils/date';
import { findLang } from '../../../common/models/lang';
import { faCalendar, faClock, faPlane, faPlaneArrival } from '@fortawesome/free-solid-svg-icons';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import moment from 'moment';
import { Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Time } from '../../../common/components/Time';

const DetailFromAirportFields = ({ transfer, onDateChange, onTimeChange, onChange, touchTransfer, transferError }) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = React.useState(false);

    const dateValue = (dateString) => {
        return (dateString && moment(dateString, 'YYYY-MM-DD')) || dateString;
    };

    // to start the week on Monday
    moment.updateLocale('en', {
        week: {
            dow: 1,
        },
    });

    // a typed date before minDate still reaches state, so the field tells the customer right away
    const minDate = minBookingDate();
    const isPast = Boolean(transfer.flight.date) && transfer.flight.date < minDate;

    return (
        <>
            <InputRow icon={faCalendar}>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <MobileDatePicker
                        open={open}
                        onClose={() => setOpen(false)}
                        sx={{ width: '100%' }}
                        name="flight.date"
                        format={datePickerFormat(findLang(i18n.language).code)}
                        value={dateValue(transfer.flight.date)}
                        minDate={moment(minDate)}
                        onChange={(date) => {
                            onDateChange({ date, name: 'flight.date' });
                            setOpen(false);
                        }}
                        required
                        slots={{ inputAdornment: () => null }}
                        slotProps={{
                            textField: {
                                error: transferError('flight.date') || isPast,
                                helperText: isPast ? t('booking.errorDateTooEarly') : undefined,
                                placeholder: t('flight.arrivalDate'),
                                onClick: () => {
                                    touchTransfer({ name: 'flight.date' });
                                    setOpen(true);
                                },
                            },
                        }}
                    />
                    <Box sx={{ position: 'absolute', right: '22px', top: '2px' }}>*</Box>
                    <Tooltip
                        title={t('tooltip.flight.arrivalDate')}
                        placement="right"
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                    >
                        <HelpOutlineOutlinedIcon
                            sx={{
                                position: 'absolute',
                                p: 0.5,
                                right: 0,
                                top: 0,
                                fontSize: '16px',
                                color: 'text.secondary',
                            }}
                        />
                    </Tooltip>
                </Box>
            </InputRow>
            <InputRow icon={faPlane}>
                <AlTextField
                    fullWidth
                    name="flight.flightNumber"
                    onBlur={touchTransfer}
                    onChange={onChange}
                    value={transfer.flight.flightNumber || ''}
                    placeholder={t('flight-number')}
                    alTooltip={t('tooltip.flight.number')}
                    required={true}
                    error={transferError('flight.flightNumber')}
                />
            </InputRow>
            <InputRow icon={faPlaneArrival}>
                <AlTextField
                    fullWidth
                    name="flight.flightDestination"
                    onBlur={touchTransfer}
                    onChange={onChange}
                    value={transfer.flight.flightDestination || ''}
                    placeholder={t('placeholder.flightOrigin')}
                    alTooltip={t('tooltip.flight.origin')}
                    required={true}
                    error={transferError('flight.flightDestination')}
                />
            </InputRow>
            <InputRow icon={faClock}>
                <Time
                    error={transferError('flight.time')}
                    value={transfer.flight.time}
                    onBlur={() => touchTransfer({ name: 'flight.time' })}
                    onChange={(time) => onTimeChange({ time, name: 'flight.time' })}
                    minutesTooltip={t('tooltip.flight.arrivalTime')}
                    required={true}
                    label={t('label.arrivalTime')}
                />
            </InputRow>
        </>
    );
};

export { DetailFromAirportFields };
